import {
  Add,
  CheckCircle,
  ContentCopy,
  DeleteOutline,
  EditOutlined,
  OpenInNew,
  Share,
  ShoppingCart,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import a2Image from 'assets/images/a2.png';
import b2Image from 'assets/images/b2.jpg';
import c2Image from 'assets/images/c2.jpg';
import v2Image from 'assets/images/v2.png';
import IdeiasPresentesPopup from 'components/IdeiasPresentesPopup';
import SaveDateFotoUpload from 'components/SaveDateFotoUpload';
import Seo from 'components/Seo';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import api from 'services/api';
import { trocarTemplateLista as trocarTemplateListaService } from 'services/adminListas';
import { confirmarPresencaPublica } from 'services/listas';
import { usePageView } from 'utils/analytics';
import { BUILD_PROFILE } from 'utils/buildProfile';
import { formatTitle, getSiteUrl } from 'utils/env';
import { recordError, recordEvent } from 'utils/observability';
import { normalizePixKeyOnBlur, PixKeyInfo } from 'utils/pix';
import { shareOrCopyLink } from 'utils/share';
import { getShareListUrl } from 'utils/shareUrl';
import { trackEvent } from 'utils/tracking';
import { getCotaSimbolicaImagem, isCotaContribuicao } from 'utils/vaquinhaCota';
import { useListaPageRules } from './ListaPage.rules';
import { PresentePublicoDTO } from './ListaPage.types';

const MODELOS_LISTA = [
  { slug: 'casamento', label: 'Casamento' },
  { slug: 'cha-de-bebe', label: 'Chá de bebê' },
  { slug: 'aniversario', label: 'Aniversário' },
  { slug: 'vaquinha', label: 'Vaquinha' },
] as const;

const ListaPage: React.FC = () => {
  const { slugLista } = useParams<{ slugLista: string }>();
  usePageView(`Lista: ${slugLista}`);

  const navigate = useNavigate();
  const location = useLocation();
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    lista,
    presentesDisponiveis: presentesDisponiveisRaw,
    presentesPresenteados,
    carregando,
    erro,
    formatarValor,
    slug,
    statusLista,
  } = useListaPageRules(slugLista, refreshKey);

  const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set());
  // Reset removed IDs whenever the hook re-fetches (e.g. after explicit triggerRefresh)
  const presentesDisponiveis = useMemo(
    () => presentesDisponiveisRaw.filter((p) => !removedIds.has(p.id)),
    [presentesDisponiveisRaw, removedIds]
  );

  // Renderização incremental dos presentes (paginação no cliente p/ mobile)
  const PRESENTES_POR_PAGINA = 24;
  const [presentesVisiveis, setPresentesVisiveis] = useState(PRESENTES_POR_PAGINA);
  const presentesSentinelaRef = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setPresentesVisiveis(PRESENTES_POR_PAGINA);
  }, [slugLista, refreshKey]);
  useEffect(() => {
    const node = presentesSentinelaRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setPresentesVisiveis((atual) => atual + PRESENTES_POR_PAGINA);
        }
      },
      { rootMargin: '700px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [presentesDisponiveis.length, presentesVisiveis]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [presenteSelecionado, setPresenteSelecionado] = useState<PresentePublicoDTO | null>(null);
  const [nomePresenteador, setNomePresenteador] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [processando, setProcessando] = useState(false);
  const [erroDialog, setErroDialog] = useState('');
  const [presencaDialogOpen, setPresencaDialogOpen] = useState(false);
  const [nomeConvidado, setNomeConvidado] = useState('');
  const [presencaProcessando, setPresencaProcessando] = useState(false);
  const [presencaErro, setPresencaErro] = useState('');
  const [presencaSucesso, setPresencaSucesso] = useState('');
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const [saveDateFotoUrl, setSaveDateFotoUrl] = useState<string | null>(null);
  const [ownerFeedback, setOwnerFeedback] = useState('');
  const [ownerError, setOwnerError] = useState('');
  const [ownerProcessing, setOwnerProcessing] = useState(false);
  const [ownerPixProcessing, setOwnerPixProcessing] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [itemRemovingId, setItemRemovingId] = useState<string | null>(null);
  const [ownerListaDetalhes, setOwnerListaDetalhes] = useState<any | null>(null);
  const [pixInput, setPixInput] = useState('');
  const [pixKeyInfo, setPixKeyInfo] = useState<PixKeyInfo | null>(null);
  const [editForm, setEditForm] = useState({
    tituloLista: '',
    templateSlug: '',
    enableRsvp: false,
    saveDateHeadline: '',
    saveDateDescription: '',
    saveDateLocation: '',
    saveDateEventDate: '',
    saveDateEventTime: '',
  });
  const [newItemForm, setNewItemForm] = useState({
    nome: '',
    descricao: '',
    valor: '',
    categoria: '',
    imagemUrl: '',
  });

  const brandTheme = BUILD_PROFILE.theme;
  const configuracoes = lista?.configuracoes ?? {};
  const configuracoesOwner = ownerListaDetalhes?.configuracoesDetalhes ?? configuracoes;
  const isOwner = Boolean(lista?.isOwner);
  const rsvpHabilitado = Boolean(configuracoesOwner.enableRsvp);
  const saveDateHabilitado = Boolean(configuracoes.enableSaveTheDate);

  const defaultSaveDateFotoUrl = useMemo(() => {
    const templateSlug = lista?.templateSlug || configuracoes?.templateSlug;
    if (templateSlug === 'aniversario') return a2Image;
    if (templateSlug === 'cha-de-bebe') return b2Image;
    if (templateSlug === 'casamento') return c2Image;
    if (templateSlug === 'vaquinha') return v2Image;
    return null;
  }, [configuracoes?.templateSlug, lista?.templateSlug]);

  const saveDateFotoExibicao = saveDateFotoUrl || defaultSaveDateFotoUrl;

  const formatIsoToDateInput = (iso?: string | null) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (v: number) => String(v).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const toIsoDateStringOrNull = (value: string) => {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  };

  const triggerRefresh = () => setRefreshKey((current) => current + 1);

  // Lotes de sugestões (+100 presentes): máx. 2 por lista, depois adição manual
  const [loteProcessando, setLoteProcessando] = useState(false);
  const templateSlugLista = lista?.templateSlug || configuracoes?.templateSlug;
  const lotesAplicados = Number(
    configuracoesOwner?.lotesSugestoesAplicados ?? configuracoes?.lotesSugestoesAplicados ?? 0
  );
  const podeAdicionarLote =
    isOwner && templateSlugLista && templateSlugLista !== 'vaquinha' && lotesAplicados < 2;

  const handleAdicionarLote = async () => {
    if (!lista?.id || loteProcessando) return;
    setLoteProcessando(true);
    setOwnerError('');
    setOwnerFeedback('');
    try {
      const resposta = await api.post<{ criados: number; lotesRestantes: number }>(
        `/lista/${lista.id}/presentes/lote-sugestoes`
      );
      setOwnerFeedback(
        `+${resposta.data.criados} presentes adicionados! ${
          resposta.data.lotesRestantes > 0
            ? `Você ainda pode adicionar mais ${resposta.data.lotesRestantes} lote.`
            : 'Limite de lotes atingido — agora é só adicionar itens manualmente.'
        }`
      );
      triggerRefresh();
    } catch (error: any) {
      setOwnerError(
        error?.response?.data?.error ?? 'Não foi possível adicionar os presentes agora.'
      );
    } finally {
      setLoteProcessando(false);
    }
  };

  // auto-open edit modal when navigated with ?openEdit=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openEdit') === '1' && isOwner && !editDialogOpen) {
      handleOpenEditDialog();
      params.delete('openEdit');
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, location.search]);

  useEffect(() => {
    if (!isOwner || !lista?.id) {
      setOwnerListaDetalhes(null);
      setPixInput('');
      return;
    }

    let cancelled = false;

    const carregarDetalhesOwner = async () => {
      try {
        const response = await api.get(`/listas/${lista.id}`);
        if (cancelled) return;
        const detalhes = response.data?.lista ?? null;
        setOwnerListaDetalhes(detalhes);
        const chavePix = String(detalhes?.pixChaveAtiva?.chave || '');
        setPixInput(chavePix);
        setPixKeyInfo(chavePix ? normalizePixKeyOnBlur(chavePix).info : null);
      } catch {
        if (cancelled) return;
        setOwnerListaDetalhes(null);
      }
    };

    void carregarDetalhesOwner();

    return () => {
      cancelled = true;
    };
  }, [isOwner, lista?.id, refreshKey]);

  const handleOpenEditDialog = () => {
    setOwnerFeedback('');
    setOwnerError('');
    setEditForm({
      tituloLista: lista?.tituloLista || '',
      templateSlug: String(templateSlugLista || 'casamento'),
      enableRsvp: Boolean(configuracoesOwner.enableRsvp),
      saveDateHeadline: String(configuracoesOwner.saveDateHeadline || ''),
      saveDateDescription: String(configuracoesOwner.saveDateDescription || ''),
      saveDateLocation: String(configuracoesOwner.saveDateLocation || ''),
      saveDateEventDate: formatIsoToDateInput(
        (configuracoesOwner.saveDateEventDate as string | null) ||
          (configuracoesOwner.rsvpEventDate as string | null) ||
          lista?.dataCasamento ||
          null
      ),
      saveDateEventTime: String(configuracoesOwner.saveDateEventTime || '19:00'),
    });
    setEditDialogOpen(true);
  };

  const handlePixChange = (valor: string) => {
    setPixInput(valor);
    setPixKeyInfo(valor.trim() ? normalizePixKeyOnBlur(valor).info : null);
  };

  const handlePixBlur = async () => {
    if (!lista?.id) return;
    const { value, info } = normalizePixKeyOnBlur(pixInput);
    if (value !== pixInput) setPixInput(value);
    setPixKeyInfo(info);
    const chave = value.trim();
    const chaveAtual = String(ownerListaDetalhes?.pixChaveAtiva?.chave || '').trim();
    if (!chave || !info.isValid || chave === chaveAtual) return;
    setOwnerPixProcessing(true);
    setOwnerError('');
    setOwnerFeedback('');
    try {
      await api.post(`/listas/${lista.id}/pix-keys`, { chavePix: chave });
      setOwnerListaDetalhes((prev: any) => {
        if (!prev) return prev;
        return { ...prev, pixChaveAtiva: { ...(prev.pixChaveAtiva ?? {}), chave, ativa: true } };
      });
      setOwnerFeedback('Chave PIX salva.');
    } catch (err: any) {
      setOwnerError(err?.response?.data?.error || 'Não foi possível salvar a chave PIX.');
    } finally {
      setOwnerPixProcessing(false);
    }
  };

  const handleSaveOwnerTexts = async () => {
    if (!lista?.id) return;
    setOwnerProcessing(true);
    setOwnerError('');
    setOwnerFeedback('');
    try {
      // Troca de modelo: atualiza os presentes sugeridos mantendo as configurações
      if (editForm.templateSlug && editForm.templateSlug !== templateSlugLista) {
        await trocarTemplateListaService(lista.id, editForm.templateSlug);
      }

      await api.put(`/listas/${lista.id}`, {
        tituloLista: editForm.tituloLista,
        enableRsvp: editForm.enableRsvp,
        enableSaveTheDate: true,
        rsvpEventDate: toIsoDateStringOrNull(editForm.saveDateEventDate),
        saveDateEventDate: toIsoDateStringOrNull(editForm.saveDateEventDate),
        saveDateEventTime: editForm.saveDateEventTime || null,
        saveDateLocation: editForm.saveDateLocation || null,
        saveDateHeadline: editForm.saveDateHeadline || null,
        saveDateDescription: editForm.saveDateDescription || null,
      });

      const chave = pixInput.trim();
      const chaveAtual = String(ownerListaDetalhes?.pixChaveAtiva?.chave || '').trim();
      const chaveRaw = chave;

      if (!chaveRaw && chaveAtual) {
        // Remover chave PIX
        await api.delete(`/listas/${lista.id}/pix-keys`);
        setOwnerListaDetalhes((prev: any) => (prev ? { ...prev, pixChaveAtiva: null } : prev));
      } else if (chaveRaw) {
        const { value: chaveNormalizada, info } = normalizePixKeyOnBlur(chaveRaw);
        if (info.isValid && chaveNormalizada !== chaveAtual) {
          if (chaveRaw !== chaveNormalizada) setPixInput(chaveNormalizada);
          setPixKeyInfo(info);
          await api.post(`/listas/${lista.id}/pix-keys`, { chavePix: chaveNormalizada });
          setOwnerListaDetalhes((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              pixChaveAtiva: {
                ...(prev.pixChaveAtiva ?? {}),
                chave: chaveNormalizada,
                ativa: true,
              },
            };
          });
        }
      }

      setOwnerFeedback('Lista atualizada com sucesso.');
      setEditDialogOpen(false);
      triggerRefresh();
    } catch (err: any) {
      setOwnerError(err?.response?.data?.error || 'Não foi possível salvar as alterações.');
    } finally {
      setOwnerProcessing(false);
    }
  };

  const handleAddItem = async () => {
    if (!lista?.id) return;
    const valorNumerico = Number(newItemForm.valor.replace(',', '.'));
    if (!newItemForm.nome.trim() || Number.isNaN(valorNumerico) || valorNumerico <= 0) {
      setOwnerError('Informe nome e valor válido para adicionar o item.');
      return;
    }

    setOwnerProcessing(true);
    setOwnerError('');
    setOwnerFeedback('');
    try {
      await api.post(`/presentes/lista/${lista.id}/presentes`, {
        nome: newItemForm.nome.trim(),
        descricao: newItemForm.descricao.trim() || null,
        valor: valorNumerico,
        categoria: newItemForm.categoria.trim() || null,
        imagemUrl: newItemForm.imagemUrl.trim() || null,
        disponivel: true,
      });
      setOwnerFeedback('Item adicionado à sua lista.');
      setNewItemForm({ nome: '', descricao: '', valor: '', categoria: '', imagemUrl: '' });
      setAddItemDialogOpen(false);
      triggerRefresh();
    } catch (err: any) {
      setOwnerError(err?.response?.data?.error || 'Não foi possível adicionar o item agora.');
    } finally {
      setOwnerProcessing(false);
    }
  };

  const handleRemoveItem = async (presenteId: string) => {
    if (!lista?.id) return;
    setItemRemovingId(presenteId);
    setOwnerError('');
    setOwnerFeedback('');
    try {
      await api.delete(`/presentes/lista/${lista.id}/presentes/${presenteId}`);
      // Remove optimistically from local state — no full re-fetch needed
      setRemovedIds((prev) => new Set(Array.from(prev).concat(presenteId)));
      setOwnerFeedback('Item removido da lista.');
    } catch (err: any) {
      setOwnerError(err?.response?.data?.error || 'Não foi possível remover este item.');
    } finally {
      setItemRemovingId(null);
    }
  };

  const handleShareList = async () => {
    if (!slug) return;
    const shareTitle = lista?.tituloLista
      ? `Veja minha lista: ${lista.tituloLista}`
      : 'Veja minha lista de presentes';
    const shareText = lista?.tituloLista
      ? `Fiz minha lista "${lista.tituloLista}". Vem ver!`
      : 'Fiz minha lista de presentes. Vem ver!';

    try {
      const outcome = await shareOrCopyLink({
        url: getShareListUrl(slug),
        title: shareTitle,
        text: shareText,
      });
      if (outcome !== 'cancelled') {
        setOwnerFeedback(
          outcome === 'shared' ? 'Menu de compartilhamento aberto.' : 'Link copiado.'
        );
      }
    } catch {
      setOwnerError('Não foi possível compartilhar agora.');
    }
  };

  const isBlobUrl = (value?: string | null): value is string =>
    typeof value === 'string' && value.startsWith('blob:');

  const responseHasImageBlob = (status: number, data: unknown): data is Blob => {
    return status !== 204 && data instanceof Blob && data.size > 0;
  };

  const dataEventoSaveDate = useMemo(() => {
    const rawDate =
      (typeof configuracoes.saveDateEventDate === 'string' && configuracoes.saveDateEventDate) ||
      (typeof configuracoes.rsvpEventDate === 'string' && configuracoes.rsvpEventDate) ||
      lista?.dataCasamento ||
      null;
    if (!rawDate) return null;

    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return null;

    const rawTime =
      typeof configuracoes.saveDateEventTime === 'string' ? configuracoes.saveDateEventTime : '';
    if (/^\d{2}:\d{2}$/.test(rawTime)) {
      const [hh, mm] = rawTime.split(':').map((v: string) => Number(v));
      date.setHours(hh, mm, 0, 0);
    }

    return date;
  }, [
    configuracoes.rsvpEventDate,
    configuracoes.saveDateEventDate,
    configuracoes.saveDateEventTime,
    lista?.dataCasamento,
  ]);

  const countdown = useMemo(() => {
    if (!dataEventoSaveDate) return null;
    const diff = dataEventoSaveDate.getTime() - countdownNow;
    if (diff <= 0) {
      return { dias: 0, horas: 0, minutos: 0, segundos: 0, finalizado: true };
    }

    const totalSeconds = Math.floor(diff / 1000);
    const dias = Math.floor(totalSeconds / (24 * 60 * 60));
    const horas = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutos = Math.floor((totalSeconds % (60 * 60)) / 60);
    const segundos = totalSeconds % 60;
    return { dias, horas, minutos, segundos, finalizado: false };
  }, [countdownNow, dataEventoSaveDate]);

  useEffect(() => {
    if (!saveDateHabilitado || !dataEventoSaveDate) return;
    const interval = window.setInterval(() => setCountdownNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [dataEventoSaveDate, saveDateHabilitado]);

  useEffect(() => {
    if (!saveDateHabilitado || !lista?.id) {
      setSaveDateFotoUrl(null);
      return;
    }

    let cancelled = false;

    const loadSaveDateFoto = async () => {
      try {
        const response = await api.get(`/listas/${lista.id}/save-date-foto/view?ts=${Date.now()}`, {
          responseType: 'blob',
        });
        if (cancelled) return;
        if (!responseHasImageBlob(response.status, response.data)) {
          setSaveDateFotoUrl((current) => {
            if (isBlobUrl(current)) URL.revokeObjectURL(current);
            return null;
          });
          return;
        }
        const blobUrl = URL.createObjectURL(response.data);
        setSaveDateFotoUrl((current) => {
          if (isBlobUrl(current)) URL.revokeObjectURL(current);
          return blobUrl;
        });
      } catch (error) {
        if (cancelled) return;
        setSaveDateFotoUrl((current) => {
          if (isBlobUrl(current)) URL.revokeObjectURL(current);
          return null;
        });
      }
    };

    void loadSaveDateFoto();

    return () => {
      cancelled = true;
    };
  }, [lista?.id, saveDateHabilitado]);

  useEffect(() => {
    return () => {
      if (isBlobUrl(saveDateFotoUrl)) {
        URL.revokeObjectURL(saveDateFotoUrl);
      }
    };
  }, [saveDateFotoUrl]);

  useEffect(() => {
    const root = document.documentElement;
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    const prevHeaderBg = root.style.getPropertyValue('--app-header-bg');
    const prevHeaderBgImage = root.style.getPropertyValue('--app-header-bg-image');
    const prevThemeColor = metaTheme?.getAttribute('content') ?? null;

    const isChaDeBebe = lista?.templateSlug === 'cha-de-bebe';
    const sexo = lista?.configuracoes?.babyShowerSubtype;

    if (!isChaDeBebe) {
      root.style.removeProperty('--app-header-bg');
      root.style.removeProperty('--app-header-bg-image');
      if (metaTheme && prevThemeColor) {
        metaTheme.setAttribute('content', prevThemeColor);
      }
      return () => {
        if (prevHeaderBg) root.style.setProperty('--app-header-bg', prevHeaderBg);
        if (prevHeaderBgImage) root.style.setProperty('--app-header-bg-image', prevHeaderBgImage);
        if (!prevHeaderBg) root.style.removeProperty('--app-header-bg');
        if (!prevHeaderBgImage) root.style.removeProperty('--app-header-bg-image');
      };
    }

    if (sexo === 'girl') {
      root.style.setProperty('--app-header-bg', '#e6a7c6');
      root.style.setProperty(
        '--app-header-bg-image',
        'radial-gradient(circle at 18% 20%, rgba(255,255,255,0.44) 0%, rgba(255,255,255,0) 40%), radial-gradient(circle at 80% 18%, rgba(255,236,244,0.6) 0%, rgba(255,236,244,0) 38%), linear-gradient(105deg, #d88db4 0%, #e6a7c6 48%, #f1c3da 100%)'
      );
      if (metaTheme) metaTheme.setAttribute('content', '#d88db4');
    } else if (sexo === 'boy') {
      root.style.setProperty('--app-header-bg', '#8eb8db');
      root.style.setProperty(
        '--app-header-bg-image',
        'radial-gradient(circle at 16% 22%, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0) 40%), radial-gradient(circle at 84% 22%, rgba(234,246,255,0.56) 0%, rgba(234,246,255,0) 36%), linear-gradient(105deg, #6a9fcd 0%, #8eb8db 48%, #b5d2eb 100%)'
      );
      if (metaTheme) metaTheme.setAttribute('content', '#6a9fcd');
    } else {
      root.style.setProperty('--app-header-bg', '#95b9c9');
      root.style.removeProperty('--app-header-bg-image');
      if (metaTheme) metaTheme.setAttribute('content', '#88adc0');
    }

    return () => {
      if (prevHeaderBg) root.style.setProperty('--app-header-bg', prevHeaderBg);
      else root.style.removeProperty('--app-header-bg');
      if (prevHeaderBgImage) root.style.setProperty('--app-header-bg-image', prevHeaderBgImage);
      else root.style.removeProperty('--app-header-bg-image');
      if (metaTheme && prevThemeColor) {
        metaTheme.setAttribute('content', prevThemeColor);
      }
    };
  }, [lista?.configuracoes?.babyShowerSubtype, lista?.templateSlug]);

  if (erro) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {slug.length === 0
          ? 'Lista não encontrada'
          : statusLista === 'failure'
            ? 'Lista não encontrada'
            : 'Erro ao carregar presentes'}
      </Alert>
    );
  }

  if (carregando) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {(templateSlugLista === 'casamento' || templateSlugLista === 'cha-de-bebe') && (
        <IdeiasPresentesPopup />
      )}
      <Seo
        title={formatTitle(lista?.tituloLista ? `Lista: ${lista.tituloLista}` : 'Minha lista')}
        description={
          lista?.tituloLista
            ? `Veja minha lista "${lista.tituloLista}" e escolha um presente com carinho.`
            : 'Veja minha lista e escolha um presente com carinho.'
        }
        canonical={slug ? `${getSiteUrl()}/${slug}` : undefined}
        ogImage={`${getSiteUrl()}${BUILD_PROFILE.assets.defaultOgImagePath}`}
        ogImageType="image/png"
        ogImageAlt="Lista de presentes"
        ogImageWidth={512}
        ogImageHeight={512}
      />
      <Box sx={{ width: '100%' }}>
        {/* Header da Lista */}
        {!saveDateHabilitado && (
          <Paper elevation={2} sx={{ p: { xs: 1.75, sm: 2.25 }, mb: 3, textAlign: 'center' }}>
            {lista?.descricao && (
              <Typography variant="body2" sx={{ mt: 1.5, maxWidth: 660, mx: 'auto' }}>
                {lista.descricao}
              </Typography>
            )}
            {rsvpHabilitado && (
              <Button
                variant="contained"
                sx={{ mt: 2, width: { xs: '100%', sm: 'auto' } }}
                onClick={() => {
                  setPresencaDialogOpen(true);
                  setPresencaErro('');
                  setPresencaSucesso('');
                }}
              >
                Confirmar presença
              </Button>
            )}
          </Paper>
        )}

        {saveDateHabilitado && (
          <>
            <Paper
              elevation={0}
              sx={{
                minHeight: { xs: '78vh', sm: '80vh', lg: 'calc(100vh - 96px)' },
                px: { xs: 2, sm: 3.5 },
                py: { xs: 3, sm: 4.5 },
                mb: 2,
                borderRadius: 4,
                border: `1px solid ${brandTheme.palette.borderSoft}`,
                color: brandTheme.palette.textPrimary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                backgroundImage: saveDateFotoExibicao
                  ? `linear-gradient(130deg, rgba(20, 12, 3, 0.48) 0%, rgba(20, 12, 3, 0.24) 42%, rgba(20, 12, 3, 0.56) 100%), radial-gradient(circle at 18% 14%, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0) 44%), url("${saveDateFotoExibicao}")`
                  : `radial-gradient(circle at 18% 14%, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0) 44%), linear-gradient(130deg, ${brandTheme.palette.heroGradientStart} 0%, ${brandTheme.palette.heroGradientEnd} 100%)`,
                backgroundSize: saveDateFotoExibicao ? 'cover, cover, cover' : 'auto',
                backgroundPosition: saveDateFotoExibicao ? 'center, center, center' : 'initial',
                boxShadow: `0 24px 45px ${brandTheme.palette.heroGlow}`,
                cursor: isOwner ? 'pointer' : 'default',
              }}
              onClick={isOwner ? handleOpenEditDialog : undefined}
            >
              <Stack spacing={2.2} sx={{ width: '100%', maxWidth: 760, mx: 'auto' }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: saveDateFotoExibicao
                      ? 'rgba(255, 244, 230, 0.92)'
                      : brandTheme.palette.secondaryMain,
                    fontWeight: 700,
                    letterSpacing: 3.4,
                    textShadow: saveDateFotoExibicao ? '0 1px 7px rgba(0,0,0,0.35)' : 'none',
                  }}
                >
                  {dataEventoSaveDate
                    ? dataEventoSaveDate
                        .toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })
                        .toUpperCase()
                    : 'SAVE THE DATE'}
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: { xs: '3rem', sm: '4.1rem', md: '5rem' },
                    lineHeight: 0.98,
                    letterSpacing: 0.4,
                    fontWeight: 500,
                    color: saveDateFotoExibicao ? '#fff7ed' : brandTheme.palette.textPrimary,
                    textShadow: saveDateFotoExibicao ? '0 3px 12px rgba(0,0,0,0.34)' : 'none',
                  }}
                >
                  {configuracoes.saveDateHeadline || 'Save the Date'}
                </Typography>

                {(configuracoes.saveDateLocation || configuracoes.saveDateEventTime) && (
                  <Stack spacing={0.6}>
                    {configuracoes.saveDateLocation && (
                      <Typography
                        sx={{
                          fontSize: { xs: '1.1rem', sm: '1.35rem' },
                          color: saveDateFotoExibicao
                            ? 'rgba(255, 247, 237, 0.98)'
                            : brandTheme.palette.textPrimary,
                          textShadow: saveDateFotoExibicao ? '0 1px 7px rgba(0,0,0,0.35)' : 'none',
                        }}
                      >
                        {configuracoes.saveDateLocation}
                      </Typography>
                    )}
                    {configuracoes.saveDateEventTime && (
                      <Typography
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.15rem' },
                          color: saveDateFotoExibicao
                            ? 'rgba(255, 240, 221, 0.95)'
                            : brandTheme.palette.textSecondary,
                          textShadow: saveDateFotoExibicao ? '0 1px 7px rgba(0,0,0,0.35)' : 'none',
                        }}
                      >
                        {configuracoes.saveDateEventTime}
                      </Typography>
                    )}
                  </Stack>
                )}

                <Typography
                  sx={{
                    mt: { xs: 1.2, sm: 2.2 },
                    fontSize: { xs: '1.1rem', sm: '1.35rem' },
                    lineHeight: 1.18,
                    fontFamily: '"Great Vibes", "Cormorant Garamond", serif',
                    letterSpacing: 0.25,
                    color: saveDateFotoExibicao
                      ? 'rgba(255, 247, 237, 0.9)'
                      : brandTheme.palette.textSecondary,
                    textShadow: saveDateFotoExibicao ? '0 1px 8px rgba(0,0,0,0.36)' : 'none',
                  }}
                >
                  {configuracoes.saveDateDescription ||
                    'Com a sua presença, este dia fica completo'}
                </Typography>
              </Stack>
            </Paper>

            {rsvpHabilitado && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  mb: 3,
                  borderRadius: 3,
                  border: `1px solid ${brandTheme.palette.borderSoft}`,
                  bgcolor: 'rgba(255, 255, 255, 0.86)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <Stack spacing={2} alignItems="center">
                  {countdown && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexWrap: 'wrap', rowGap: 1, justifyContent: 'center' }}
                    >
                      {[
                        { label: 'dias', value: countdown.dias },
                        { label: 'horas', value: countdown.horas },
                        { label: 'min', value: countdown.minutos },
                        { label: 'seg', value: countdown.segundos },
                      ].map((item) => (
                        <Paper
                          key={item.label}
                          sx={{
                            minWidth: 68,
                            textAlign: 'center',
                            py: 1,
                            px: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${brandTheme.palette.borderSoft}`,
                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                            color: brandTheme.palette.secondaryMain,
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                            {item.value}
                          </Typography>
                          <Typography variant="caption">{item.label}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  )}

                  {rsvpHabilitado && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} width="100%">
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{
                          bgcolor: brandTheme.palette.primaryMain,
                          color: '#fff',
                          '&:hover': { bgcolor: brandTheme.palette.secondaryMain },
                        }}
                        onClick={() => {
                          setPresencaDialogOpen(true);
                          setPresencaErro('');
                          setPresencaSucesso('');
                        }}
                      >
                        Confirmar presença
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            )}
          </>
        )}

        {isOwner && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: { xs: 1.6, sm: 2.1 },
              borderRadius: 3,
              border: `1px solid ${brandTheme.palette.borderSoft}`,
              background:
                'linear-gradient(170deg, rgba(255,255,255,0.98) 0%, rgba(255,251,244,0.97) 100%)',
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Seu espaço
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<EditOutlined />}
                  onClick={handleOpenEditDialog}
                >
                  Editar lista
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => {
                    if (lista?.id) navigate(`/admin/listas/${lista.id}/presentes`);
                  }}
                >
                  Gerenciar presentes
                </Button>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(getShareListUrl(slug));
                      setOwnerFeedback('Link copiado.');
                    } catch {
                      setOwnerError('Não foi possível copiar o link.');
                    }
                  }}
                >
                  Copiar link
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Share />}
                  onClick={() => void handleShareList()}
                >
                  Compartilhar
                </Button>
              </Stack>

              {ownerError && <Alert severity="error">{ownerError}</Alert>}
              {ownerFeedback && <Alert severity="success">{ownerFeedback}</Alert>}
            </Stack>
          </Paper>
        )}

        <Stack
          id="lista-de-presentes"
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: 4, mb: 1 }}
        >
          <Typography variant="h5">Lista de Presentes ({presentesDisponiveis.length})</Typography>
          {podeAdicionarLote && (
            <Tooltip
              title={`Adicionar +100 presentes sugeridos (${2 - lotesAplicados} restante${2 - lotesAplicados > 1 ? 's' : ''})`}
            >
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                disabled={loteProcessando}
                onClick={() => void handleAdicionarLote()}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0.1,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: 'none',
                  lineHeight: 1.6,
                }}
              >
                {loteProcessando ? <CircularProgress size={12} /> : '+100'}
              </Button>
            </Tooltip>
          )}
        </Stack>

        {presentesDisponiveis.length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            Todos os presentes já foram escolhidos! ❤️
          </Alert>
        ) : (
          <>
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
              }}
              gap={{ xs: 1.25, md: 1.75 }}
              sx={{ mb: 2 }}
            >
              {presentesDisponiveis.slice(0, presentesVisiveis).map((presente) => (
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: `1px solid ${brandTheme.palette.borderSoft}`,
                    backgroundColor: 'rgba(255,255,255,0.92)',
                    overflow: 'hidden',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 14px 26px rgba(39, 27, 15, 0.12)',
                    },
                  }}
                  key={presente.id}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        bgcolor: '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {presente.imagemUrl ? (
                        <Box
                          component="img"
                          src={presente.imagemUrl}
                          alt={presente.nome}
                          loading="lazy"
                          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }}
                        />
                      ) : isCotaContribuicao(presente.categoria) ? (
                        <Box
                          component="img"
                          src={getCotaSimbolicaImagem(presente.valor)}
                          alt={presente.nome}
                          loading="lazy"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '2.5rem' }}>🎁</Typography>
                      )}
                    </Box>
                    {isOwner && (
                      <Tooltip title="Remover item">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={itemRemovingId === presente.id}
                          onClick={() => void handleRemoveItem(presente.id)}
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            bgcolor: 'rgba(255,255,255,0.88)',
                            backdropFilter: 'blur(3px)',
                            '&:hover': { bgcolor: 'rgba(255,235,235,0.96)' },
                            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                          }}
                        >
                          {itemRemovingId === presente.id ? (
                            <CircularProgress size={16} color="error" />
                          ) : (
                            <DeleteOutline fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <CardContent
                    sx={{ flexGrow: 1, p: { xs: 1.25, md: 1.5 }, pb: { xs: 0.5, md: 0.75 } }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.82rem', md: '0.9rem' },
                        fontWeight: 600,
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: { xs: '2.15rem', md: '2.35rem' },
                      }}
                    >
                      {presente.nome}
                    </Typography>
                    {presente.descricao && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.35,
                          mt: 0.25,
                        }}
                      >
                        {presente.descricao}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: brandTheme.palette.primaryMain,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        mt: 0.5,
                      }}
                    >
                      {formatarValor(presente.valor)}
                    </Typography>
                    {presente.produtoAfiliado?.parceiro && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        em {presente.produtoAfiliado.parceiro}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions
                    disableSpacing
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 0.6,
                      px: { xs: 1.25, md: 1.5 },
                      pb: { xs: 1.25, md: 1.5 },
                      pt: 0,
                      mt: 'auto',
                      '& > *': { marginLeft: '0 !important' },
                    }}
                  >
                    {presente.produtoAfiliado?.urlAfiliado && (
                      <Button
                        size="small"
                        variant="text"
                        color="secondary"
                        endIcon={<OpenInNew sx={{ fontSize: 12 }} />}
                        sx={{
                          fontSize: '0.68rem',
                          py: 0.2,
                          minHeight: 22,
                          textTransform: 'none',
                          opacity: 0.8,
                          borderRadius: 2,
                          alignSelf: 'end',
                          px: 1,
                          mb: 1,
                          border: `1px solid ${brandTheme.palette.borderSoft}`,
                        }}
                        onClick={() => {
                          const url = presente.produtoAfiliado?.urlAfiliado;
                          if (url) {
                            trackEvent('comprar', {
                              slugLista,
                              presenteId: presente.id,
                              parceiro: presente.produtoAfiliado?.parceiro,
                            });
                            recordEvent('item_reserved', {
                              listId: slugLista,
                              itemId: presente.id,
                              itemTitle: presente.nome,
                              method: 'affiliate_link',
                              partner: presente.produtoAfiliado?.parceiro,
                            });
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        Comprar
                      </Button>
                    )}
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      startIcon={<ShoppingCart sx={{ fontSize: 15 }} />}
                      sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                      onClick={() => {
                        try {
                          Sentry.logger.info('click_presentear', {
                            slugLista,
                            presenteId: presente.id,
                          });
                        } catch (err) {
                          // ignore
                        }
                        setPresenteSelecionado(presente);
                        setDialogOpen(true);
                        setErroDialog('');
                        setMensagem('');
                        setNomePresenteador('');
                      }}
                    >
                      Presentear
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
            {/* Sentinela: carrega mais presentes ao chegar perto do fim */}
            {presentesDisponiveis.length > presentesVisiveis && (
              <>
                <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
                  <CircularProgress size={26} />
                </Box>
                <Box ref={presentesSentinelaRef} sx={{ height: 1, mb: 4 }} />
              </>
            )}
          </>
        )}

        {/* Presentes Já Presenteados */}
        {presentesPresenteados.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h5" gutterBottom>
              Presentes Já Escolhidos ({presentesPresenteados.length})
            </Typography>
            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={3}>
              {presentesPresenteados.map((presente) => (
                <Card elevation={1} sx={{ opacity: 0.7 }} key={presente.id}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CheckCircle color="success" />
                      <Typography variant="h6">{presente.nome}</Typography>
                    </Box>
                    <Typography color="text.secondary" paragraph>
                      {presente.descricao}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" color="primary">
                        {formatarValor(presente.valor)}
                      </Typography>
                    </Box>
                    {presente.presenteadorNome && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                        Presenteado por: {presente.presenteadorNome}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={presencaDialogOpen}
        onClose={() => !presencaProcessando && setPresencaDialogOpen(false)}
        fullWidth
      >
        <DialogTitle>Confirmar presença</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Você é nosso convidado especial! Digite seu nome para confirmar sua presença.
          </Typography>
          <TextField
            fullWidth
            label="Seu nome"
            value={nomeConvidado}
            onChange={(event) => setNomeConvidado(event.target.value)}
            disabled={presencaProcessando}
            autoFocus
          />
          {presencaErro && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {presencaErro}
            </Alert>
          )}
          {presencaSucesso && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {presencaSucesso}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresencaDialogOpen(false)} disabled={presencaProcessando}>
            Fechar
          </Button>
          <Button
            variant="contained"
            disabled={presencaProcessando || !nomeConvidado.trim() || !slug}
            onClick={async () => {
              if (!slug) return;
              const nome = nomeConvidado.trim();
              if (nome.length < 2) {
                setPresencaErro('Digite um nome valido.');
                return;
              }

              setPresencaProcessando(true);
              setPresencaErro('');
              setPresencaSucesso('');
              try {
                await confirmarPresencaPublica(slug, { nome });
                setNomeConvidado('');
                setPresencaSucesso('Presença confirmada com sucesso!');
                setPresencaDialogOpen(false);
              } catch (err: any) {
                const message =
                  err?.response?.data?.error || 'Não foi possível confirmar sua presença agora.';
                setPresencaErro(message);
              } finally {
                setPresencaProcessando(false);
              }
            }}
          >
            {presencaProcessando ? 'Confirmando...' : 'Confirmar presença'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => !processando && setDialogOpen(false)}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${brandTheme.palette.borderSoft}`,
            backgroundColor: 'rgba(255,255,255,0.97)',
          },
        }}
      >
        <DialogTitle>Presentear {presenteSelecionado?.nome}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Preencha seus dados para gerar o PIX e presentear quem criou a lista{' '}
            {lista?.tituloLista}
          </Typography>
          <TextField
            fullWidth
            label="Seu nome completo"
            value={nomePresenteador}
            onChange={(e) => setNomePresenteador(e.target.value)}
            required
            sx={{ mb: 2 }}
            disabled={processando}
          />
          <TextField
            fullWidth
            label="Mensagem para quem vai receber (opcional)"
            multiline
            rows={3}
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            placeholder={`Desejamos felicidades para ${lista?.tituloLista || 'você'}!`}
            sx={{ mb: 1 }}
            disabled={processando}
          />
          {erroDialog && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {erroDialog}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={processando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={processando ? <CircularProgress size={20} /> : <ShoppingCart />}
            disabled={processando || !nomePresenteador.trim()}
            onClick={async () => {
              if (!slugLista || !presenteSelecionado?.id) return;
              if (!nomePresenteador.trim()) {
                setErroDialog('Preencha seu nome');
                return;
              }
              setProcessando(true);
              setErroDialog('');
              try {
                trackEvent('gerar_pix', {
                  slugLista,
                  presenteId: presenteSelecionado.id,
                });
                recordEvent('checkout_started', {
                  listId: slugLista,
                  itemId: presenteSelecionado.id,
                  itemTitle: presenteSelecionado.nome,
                  itemValue: presenteSelecionado.valor,
                  giverName: nomePresenteador.trim(),
                });
                const payload = {
                  nomePresenteador: nomePresenteador.trim(),
                  mensagem: (mensagem || `Presente para ${lista?.tituloLista || 'vocês'}`).trim(),
                };
                const response = await api.post(
                  `/webhook/gerar-pix/${slugLista}/${presenteSelecionado.id}`,
                  payload
                );
                const transacaoId = response.data?.transacao?.id as string | undefined;
                if (transacaoId) {
                  recordEvent('checkout_completed', {
                    listId: slugLista,
                    itemId: presenteSelecionado.id,
                    itemTitle: presenteSelecionado.nome,
                    transactionId: transacaoId,
                  });
                  navigate(`/pagamento/${transacaoId}`);
                } else {
                  setErroDialog('Não foi possível gerar o PIX. Tente novamente.');
                  recordError(new Error('PIX generation returned no transactionId'), {
                    action: 'gerar_pix',
                    listId: slugLista,
                    itemId: presenteSelecionado.id,
                  });
                }
              } catch (err: any) {
                if (err?.response?.status === 400) {
                  setErroDialog(err.response.data?.error || 'Dados inválidos');
                } else if (err?.response?.status === 404) {
                  setErroDialog('Presente não encontrado ou já foi escolhido.');
                } else {
                  setErroDialog('Erro ao gerar PIX. Tente novamente.');
                }
                recordError(err, {
                  action: 'gerar_pix',
                  listId: slugLista,
                  itemId: presenteSelecionado?.id,
                  giverName: nomePresenteador.trim(),
                });
              } finally {
                setProcessando(false);
              }
            }}
          >
            {processando
              ? 'Gerando PIX...'
              : `Presentear - ${presenteSelecionado && formatarValor(presenteSelecionado.valor)}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => !ownerProcessing && setEditDialogOpen(false)}
        fullWidth
      >
        <DialogTitle>Editar lista</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <TextField
              label="Titulo da lista"
              value={editForm.tituloLista}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, tituloLista: event.target.value }))
              }
              fullWidth
            />
            <TextField
              select
              label="Modelo da lista"
              value={editForm.templateSlug}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, templateSlug: event.target.value }))
              }
              fullWidth
            >
              {MODELOS_LISTA.map((modelo) => (
                <MenuItem key={modelo.slug} value={modelo.slug}>
                  {modelo.label}
                </MenuItem>
              ))}
            </TextField>
            {editForm.templateSlug && editForm.templateSlug !== templateSlugLista && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                Ao salvar, os presentes serão atualizados para o modelo de{' '}
                <strong>
                  {MODELOS_LISTA.find((modelo) => modelo.slug === editForm.templateSlug)?.label}
                </strong>{' '}
                e suas configurações serão mantidas. Presentes que já receberam contribuição não
                são removidos.
              </Alert>
            )}
            <TextField
              label="Chave PIX para receber"
              value={pixInput}
              onChange={(event) => handlePixChange(event.target.value)}
              onBlur={() => void handlePixBlur()}
              fullWidth
              disabled={ownerPixProcessing}
              placeholder="E-mail, telefone, CPF/CNPJ ou chave aleatória"
              error={pixKeyInfo?.type === 'Invalida' && !pixKeyInfo.isValid}
              slotProps={{
                input: {
                  endAdornment: ownerPixProcessing ? (
                    <CircularProgress size={18} />
                  ) : pixKeyInfo && pixKeyInfo.type !== 'Vazia' ? (
                    <Chip
                      size="small"
                      color={pixKeyInfo.isValid ? 'success' : 'error'}
                      label={
                        pixKeyInfo.type === 'Invalida'
                          ? 'Chave desconhecida'
                          : pixKeyInfo.type === 'Aleatoria'
                            ? 'Aleatória'
                            : pixKeyInfo.type
                      }
                    />
                  ) : null,
                },
              }}
            />
            <TextField
              label="Frase principal"
              value={editForm.saveDateHeadline}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, saveDateHeadline: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Mensagem"
              value={editForm.saveDateDescription}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, saveDateDescription: event.target.value }))
              }
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Local"
              value={editForm.saveDateLocation}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, saveDateLocation: event.target.value }))
              }
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Data"
                type="date"
                value={editForm.saveDateEventDate}
                onChange={(event) =>
                  setEditForm((prev) => ({ ...prev, saveDateEventDate: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Hora"
                type="time"
                value={editForm.saveDateEventTime}
                onChange={(event) =>
                  setEditForm((prev) => ({ ...prev, saveDateEventTime: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.enableRsvp}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, enableRsvp: event.target.checked }))
                  }
                />
              }
              label="Confirmação de presença ativa"
              sx={{ m: 0 }}
            />
            {lista?.id && (
              <SaveDateFotoUpload
                listaId={lista.id}
                isOpen={editDialogOpen}
                onSuccess={(photoUrl) => {
                  setSaveDateFotoUrl(photoUrl);
                  setOwnerFeedback('Foto atualizada com sucesso.');
                }}
                onError={(message) => setOwnerError(message)}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={ownerProcessing}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleSaveOwnerTexts()}
            variant="contained"
            disabled={ownerProcessing}
          >
            {ownerProcessing ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addItemDialogOpen}
        onClose={() => !ownerProcessing && setAddItemDialogOpen(false)}
        fullWidth
      >
        <DialogTitle>Adicionar item na lista</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <TextField
              label="Nome do item"
              value={newItemForm.nome}
              onChange={(event) =>
                setNewItemForm((prev) => ({ ...prev, nome: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Descricao"
              value={newItemForm.descricao}
              onChange={(event) =>
                setNewItemForm((prev) => ({ ...prev, descricao: event.target.value }))
              }
              multiline
              rows={2}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Valor"
                value={newItemForm.valor}
                onChange={(event) =>
                  setNewItemForm((prev) => ({ ...prev, valor: event.target.value }))
                }
                placeholder="Ex.: 199.90"
                fullWidth
              />
              <TextField
                label="Categoria"
                value={newItemForm.categoria}
                onChange={(event) =>
                  setNewItemForm((prev) => ({ ...prev, categoria: event.target.value }))
                }
                fullWidth
              />
            </Stack>
            <TextField
              label="URL da imagem (opcional)"
              value={newItemForm.imagemUrl}
              onChange={(event) =>
                setNewItemForm((prev) => ({ ...prev, imagemUrl: event.target.value }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDialogOpen(false)} disabled={ownerProcessing}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleAddItem()}
            variant="contained"
            disabled={ownerProcessing}
          >
            {ownerProcessing ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ListaPage;
